import { Config } from '@/config';
import { ApiService } from '@/services/api.service';
import { formatDate, toMoment, getDaysBeforeClosing } from '@/util/date-util';
import axios from 'axios';

const PENDING_VOTE_ID = 'pending';

// The backend returns OpportunityAssigneeSummary records that carry their own record `id`;
// drop it so assignees match the tender assignee shape BaseAssignation matches selection on
// (it maps `userId` to the menu item id, which a leftover `id` field would clobber).
const toAssignee = ({ userId, email, firstName, lastName, assignedDate }) => ({
  userId,
  email,
  firstName,
  lastName,
  assignedDate,
});

// Label assignments carry their own record `id`; drop it so labels match on `labelId`,
// which is what BaseLabelsMenu maps to the menu item id (same reasoning as `toAssignee`).
const toOpportunityLabel = ({ labelId, name, description, color }) => ({
  labelId,
  name,
  description,
  color,
});

const CancelToken = axios.CancelToken;
let cancelTokens = {
  opportunities: null,
  addOpportunity: null,
};

class OpportunityService {
  getOpportunitiesByTenders({ tenderIds, pageSize, cancelToken = null }) {
    return ApiService.post(
      `${Config.api.document.url}api/v1/opportunities/search?pageSize=${pageSize}`,
      {
        authorId: null,
        hasDocuments: null,
        tenderId: tenderIds,
        opportunityStatusId: null,
        voterId: null,
        hasPendingVote: null,
        assigneeId: null,
        labelId: null,
        query: null,
      },
      {
        cancelToken: cancelToken && cancelToken.token,
      },
    ).then((response) => response.data);
  }

  getOpportunities({ page, sortingField, sortingOrder, filters, pageSize, cancelToken = null }) {
    // When `cancelToken` is provided, the caller owns cancellation (used by ad-hoc pickers
    // that mustn't stomp on other in-flight `getOpportunities` calls). When omitted, fall
    // back to the legacy shared-slot behavior — suited to the single main Opportunities
    // list page, which wants to cancel its own previous request on each pagination/sort.
    let token;
    if (cancelToken) {
      token = cancelToken.token;
    } else {
      if (cancelTokens.opportunities) cancelTokens.opportunities.cancel('Multiple requests.');
      cancelTokens.opportunities = CancelToken.source();
      token = cancelTokens.opportunities.token;
    }

    const voterIds = filters && filters.voterId ? filters.voterId.filter((id) => id !== PENDING_VOTE_ID) : null;
    const hasPendingVote = filters && filters.voterId && filters.voterId.includes(PENDING_VOTE_ID) ? true : null;

    const pageSizeParam = pageSize != null ? `&pageSize=${pageSize}` : '';
    return ApiService.post(
      `${Config.api.document.url}api/v1/opportunities/search/?page=${page}&sortingField=${sortingField}&sortingOrder=${sortingOrder}${pageSizeParam}`,
      {
        authorId: filters && filters.authorId ? filters.authorId : null,
        hasDocuments: filters && filters.hasDocuments !== undefined ? filters.hasDocuments : null,
        tenderId: filters && filters.tenderId ? filters.tenderId : null,
        opportunityStatusId: filters && filters.opportunityStatusId ? filters.opportunityStatusId : null,
        voterId: voterIds && voterIds.length > 0 ? voterIds : null,
        hasPendingVote,
        assigneeId: filters && filters.assigneeId && filters.assigneeId.length > 0 ? filters.assigneeId : null,
        labelId: filters && filters.labelId && filters.labelId.length > 0 ? filters.labelId : null,
        query: filters && filters.query ? filters.query : null,
      },
      {
        cancelToken: token,
      },
    ).then((response) => {
      const opportunities = response.data.items.map((item) => ({
        ...item,
        assignees: item.assignees ? item.assignees.map(toAssignee) : [],
        labels: item.labels ? item.labels.map(toOpportunityLabel) : [],
        daysBeforeClosing: getDaysBeforeClosing(item.endDate),
        tender: item.tender
          ? {
              ...item.tender,
              daysBeforeClosing: getDaysBeforeClosing(
                item.tender.tenderPeriod ? item.tender.tenderPeriod.endDate : null,
              ),
            }
          : item.tender,
      }));
      const pagination = {
        page: response.data.page,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages,
      };
      return { opportunities, pagination };
    });
  }

  getOpportunity({ id, cancelToken = null }) {
    return ApiService.get(`${Config.api.document.url}api/v1/opportunities/${id}`, {
      cancelToken: cancelToken && cancelToken.token,
    }).then((response) => {
      const opportunity = response.data;
      return {
        ...opportunity,
        assignees: opportunity.assignees ? opportunity.assignees.map(toAssignee) : [],
        labels: opportunity.labels ? opportunity.labels.map(toOpportunityLabel) : [],
      };
    });
  }

  addOpportunity(newOpportunity) {
    if (cancelTokens.addOpportunity) cancelTokens.addOpportunity.cancel('Multiple requests.');
    cancelTokens.addOpportunity = CancelToken.source();
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities`, newOpportunity, {
      cancelToken: cancelTokens.addOpportunity.token,
    }).then((response) => response.data);
  }

  editOpportunity({ opportunityId, opportunity }) {
    return ApiService.put(`${Config.api.document.url}api/v1/opportunities/${opportunityId}`, opportunity).then(
      (response) => {
        const updated = response.data;
        if (!updated.assignees) return updated;
        return {
          ...updated,
          assignees: updated.assignees.map(toAssignee),
        };
      },
    );
  }

  addOpportunities(tenderIds) {
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities/batch-create`, tenderIds).then(
      (response) => response.data,
    );
  }

  deleteOpportunity({ id }) {
    return ApiService.delete(`${Config.api.document.url}api/v1/opportunities/${id}`).then((response) => response.data);
  }

  deleteMany(ids) {
    return ApiService.delete(`${Config.api.document.url}api/v1/opportunities/batch-delete`, { data: ids }).then(
      (response) => response.data,
    );
  }
  updateStatusMany({ newOpportunityStatusId, filters }) {
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities/batch-update-status`, {
      newOpportunityStatusId,
      filters: {
        statusId: filters.statusId || null,
        opportunityIds: filters.opportunityIds || null,
      },
    }).then((response) => response.data);
  }

  assign({ opportunityId, userIds }) {
    return ApiService.put(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/assignees`, {
      userIds,
    }).then((response) => response.data.map(toAssignee));
  }

  getAssignees({ opportunityId }) {
    return ApiService.get(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/assignees`).then(
      (response) => response.data.map(toAssignee),
    );
  }

  getAssignee({ opportunityId, id }) {
    return ApiService.get(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/assignees/${id}`).then(
      (response) => response.data,
    );
  }

  deleteAssignee({ opportunityId, id }) {
    return ApiService.delete(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/assignees/${id}`).then(
      (response) => response.data,
    );
  }

  assignLabels({ opportunityId, labelIds }) {
    return ApiService.put(`${Config.api.document.url}api/v1/opportunities/${opportunityId}/labels`, {
      labelIds,
    }).then((response) => response.data.map(toOpportunityLabel));
  }

  share({ id, recipientIds }) {
    return ApiService.post(`${Config.api.document.url}api/v1/opportunities/${id}/share`, { recipientIds }).then(
      (response) => response.data,
    );
  }
}

export const opportunityService = new OpportunityService();
